/**
 * HERO mock: folder import — drop a folder, AI assigns kinds, then matches
 * master data. Plays once per visit of the view.
 */
"use client";

import { ld } from "@/lib/theme/light-dark";
import { Check, Folder, FolderInput, Loader2, Sparkles } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT } from "../../hero-tokens";

/** 0 = AI reads folders, 1 = kinds set, 2 = master data matched. */
type ImportStage = 0 | 1 | 2;

const STAGE_DELAYS_MS: readonly [number, number] = [1400, 2600];

/** Kind colours of the real mapping (`folder-kind-menu.tsx`). */
const KIND_COLOR = { property: "#45599E", unit: "#D07A22" } as const;

type RowKey = "root" | "weg" | "we01" | "etv" | "hafen" | "goethe" | "archiv";

const ROWS: ReadonlyArray<{
  readonly key: RowKey;
  readonly depth: number;
  readonly kind: "property" | "unit" | "none" | "ignore";
  readonly files: number | null;
}> = [
  { key: "root", depth: 0, kind: "none", files: null },
  { key: "weg", depth: 1, kind: "property", files: 6 },
  { key: "we01", depth: 2, kind: "unit", files: 1 },
  { key: "etv", depth: 2, kind: "none", files: 2 },
  { key: "hafen", depth: 1, kind: "property", files: 5 },
  { key: "goethe", depth: 1, kind: "property", files: 2 },
  { key: "archiv", depth: 1, kind: "ignore", files: 1 },
];

const STEP_KEYS = ["schrittAblegen", "schrittZuordnen", "schrittPruefen", "schrittImport"] as const;

/** Static keys — a template literal would hide them from `lint:i18n`. */
const FOLDER_KEYS: Readonly<Record<RowKey, string>> = {
  root: "ordner.root",
  weg: "ordner.weg",
  we01: "ordner.we01",
  etv: "ordner.etv",
  hafen: "ordner.hafen",
  goethe: "ordner.goethe",
  archiv: "ordner.archiv",
};

/** Matched record per assigned row; rows without a kind have none. */
const MATCH_KEYS: Readonly<Partial<Record<RowKey, string>>> = {
  weg: "match.weg",
  we01: "match.we01",
  hafen: "match.hafen",
  goethe: "match.goethe",
};

/** Name column width at depth 0; deeper rows shrink it so later columns line up. */
const NAME_COL = 340;
const INDENT = 14;

function rowColumns(depth: number): string {
  return `${NAME_COL - depth * INDENT}px 1fr 52px`;
}

const columnLabel: CSSProperties = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  color: H.inkGhost,
  textTransform: "uppercase",
};

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useImportStage(): ImportStage {
  const [stage, setStage] = useState<ImportStage>(0);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setStage(2);
      return;
    }
    const first = window.setTimeout(() => setStage(1), STAGE_DELAYS_MS[0]);
    const second = window.setTimeout(() => setStage(2), STAGE_DELAYS_MS[0] + STAGE_DELAYS_MS[1]);
    return () => {
      window.clearTimeout(first);
      window.clearTimeout(second);
    };
  }, []);
  return stage;
}

function ImportSteps(props: { readonly current: number }): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteImport");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", marginBottom: 16 }}>
      {STEP_KEYS.map((key, index) => {
        const past = index < props.current;
        const active = index === props.current;
        return (
          <div key={key} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            {index > 0 ? (
              <span
                style={{
                  position: "absolute",
                  top: 11,
                  left: 0,
                  width: "50%",
                  height: 2,
                  background: props.current >= index ? "rgba(0,128,112,0.4)" : ld("#E7E9ED", "#32363a"),
                }}
              />
            ) : null}
            {index < STEP_KEYS.length - 1 ? (
              <span
                style={{
                  position: "absolute",
                  top: 11,
                  right: 0,
                  width: "50%",
                  height: 2,
                  background: props.current > index ? "rgba(0,128,112,0.4)" : ld("#E7E9ED", "#32363a"),
                }}
              />
            ) : null}
            <span
              style={{
                position: "relative",
                zIndex: 1,
                width: 22,
                height: 22,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
                background: past ? "#008070" : active ? H.ink : ld("#fff", "#181b1f"),
                color: past || active ? ld("#fff", "#181b1f") : H.inkDim,
                boxShadow: past || active ? "none" : `inset 0 0 0 1px ${ld("#E2E5EA", "#36393d")}`,
                transition: "background 300ms ease",
              }}
            >
              {past ? <Check width={12} height={12} strokeWidth={2.4} /> : index + 1}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: active ? 600 : 400, color: active ? H.ink : H.inkMute }}>
              {t(key)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AssignmentCell(props: {
  readonly rowKey: RowKey;
  readonly kind: (typeof ROWS)[number]["kind"];
  readonly stage: ImportStage;
  readonly order: number;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteImport");
  if (props.kind === "ignore") {
    return <span style={{ fontSize: 12.5, color: H.inkGhost }}>{t("nichtImportieren")}</span>;
  }
  if (props.stage === 0) {
    return <span style={{ display: "block", width: 112, height: 12, borderRadius: 6, background: "rgba(0,128,112,0.12)" }} />;
  }
  if (props.kind === "none") {
    return <span style={{ fontSize: 12.5, color: H.inkGhost }}>{t("keineZuordnung")}</span>;
  }
  const color = KIND_COLOR[props.kind];
  const matchKey = MATCH_KEYS[props.rowKey];
  const matched = props.stage === 2 && matchKey !== undefined;
  return (
    <span
      className="mkhero-import-assign"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
        fontSize: 12.5,
        animationDelay: `${props.order * 140}ms`,
      }}
    >
      <span style={{ color: ld("#008070", "#5cbfad"), display: "inline-flex", flexShrink: 0 }}>
        {matched ? <Check width={12} height={12} strokeWidth={2.4} /> : <Sparkles width={12} height={12} strokeWidth={2} />}
      </span>
      <span style={{ color, fontWeight: matched ? 600 : 500, flexShrink: 0 }}>
        {props.kind === "property" ? t("liegenschaft") : t("einheit")}
      </span>
      {matched ? (
        <span style={{ color: H.inkSoft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {t(matchKey)}
        </span>
      ) : null}
    </span>
  );
}

export function DokumenteImportView(): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteImport");
  const stage = useImportStage();
  let kindOrder = 0;

  return (
    <section
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        height: MODULE_HEIGHT,
        padding: "18px 22px 0",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @keyframes mkheroImportIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        .mkhero-import-assign { animation: mkheroImportIn 380ms ease-out both; }
        @keyframes mkheroImportSpin { to { transform: rotate(360deg); } }
        .mkhero-import-spin { animation: mkheroImportSpin 900ms linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .mkhero-import-assign, .mkhero-import-spin { animation: none; }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 9, fontSize: 18, fontWeight: 700, color: H.ink }}>
          <span style={{ color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"), display: "inline-flex" }}>
            <FolderInput width={18} height={18} strokeWidth={1.9} />
          </span>
          {t("titel")}
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 500,
            color: ld("#00695C", "#6abdad"),
            background: "rgba(0,128,112,0.08)",
          }}
        >
          {stage === 2 ? (
            <Check width={13} height={13} strokeWidth={2.2} />
          ) : (
            <Loader2 className="mkhero-import-spin" width={13} height={13} strokeWidth={2.2} />
          )}
          {stage === 0 ? t("kiAnalysiert") : stage === 1 ? t("kiGleichtAb") : t("abgeglichen")}
        </span>
      </div>

      <ImportSteps current={stage === 2 ? 2 : 1} />

      <div style={{ fontSize: 13, fontWeight: 600, color: H.ink }}>{t("ordnerName")}</div>
      <div style={{ fontSize: 11.5, color: H.inkDim, marginBottom: 10 }}>{t("ordnerMeta")}</div>

      <div style={{ display: "grid", gridTemplateColumns: rowColumns(0), padding: "0 12px 6px" }}>
        <span style={columnLabel}>{t("spalteName")}</span>
        <span style={columnLabel}>{t("spalteZuordnung")}</span>
        <span style={{ ...columnLabel, textAlign: "right" }}>{t("spalteDateien")}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {ROWS.map((row) => {
          const ignored = row.kind === "ignore";
          const order = row.kind === "property" || row.kind === "unit" ? kindOrder++ : 0;
          return (
            <div
              key={row.key}
              style={{
                display: "grid",
                gridTemplateColumns: rowColumns(row.depth),
                alignItems: "center",
                height: 34,
                padding: "0 12px",
                marginLeft: row.depth * INDENT,
                borderRadius: 8,
                background: ld("#F7F8FA", "#222528"),
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span
                  style={{
                    width: 13,
                    height: 13,
                    flexShrink: 0,
                    borderRadius: 3,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: ignored ? ld("#fff", "#181b1f") : H.ink,
                    boxShadow: ignored ? `inset 0 0 0 1.5px ${ld("#C9CDD4", "#4b4e52")}` : "none",
                    color: ld("#fff", "#181b1f"),
                  }}
                >
                  {ignored ? null : <Check width={9} height={9} strokeWidth={3} />}
                </span>
                <span style={{ color: ignored ? H.inkGhost : ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"), display: "inline-flex", flexShrink: 0 }}>
                  <Folder width={14} height={14} strokeWidth={1.8} />
                </span>
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: row.depth === 0 ? 600 : 500,
                    color: ignored ? H.inkGhost : H.ink,
                    textDecoration: ignored ? "line-through" : "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t(FOLDER_KEYS[row.key])}
                </span>
              </span>
              <span style={{ minWidth: 0, overflow: "hidden" }}>
                <AssignmentCell rowKey={row.key} kind={row.kind} stage={stage} order={order} />
              </span>
              <span style={{ fontSize: 12, color: H.inkGhost, textAlign: "right" }}>{row.files ?? ""}</span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          justifyContent: "flex-end",
          padding: "12px 0 16px",
          borderTop: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 9,
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            background: "#008070",
            opacity: stage === 2 ? 1 : 0.5,
            transition: "opacity 300ms ease",
          }}
        >
          {t("importStarten")}
        </span>
      </div>
    </section>
  );
}
